import React, { useState, useCallback, useRef, useMemo, useEffect, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import serveRequestErrors from "common/serve-request-errors";
import sanitizeObject from "common/sanitize-object";
import api from "services/api";
import AppSearchInput from "components/app-search-input";
import AppEmptyState from "components/app-empty-state";
import AppTable, { AppTableCell, AppTableMoreIcon } from "components/app-table";
import AppReminderTemplateModal from "components/pages/e-learning/app-reminder-template-modal";
import COMMON from "common";
import AppStatus from "components/app-status";
import { Menu, MenuItem } from "@mui/material";
import addIcon from "assets/images/add-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import editIcon from "assets/images/edit-icon.svg";
import AppConfirmDeleteTemplateModal from "components/pages/e-learning/app-confirm-delete-template-modal";

const PageReminderTemplate = (props) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const memoSetSearchParams = useRef(setSearchParams);
    const modalRef = useRef();
    const confirmDeleteRef = useRef();
    const paramsRef = useRef({
        page: parseInt(searchParams.get("page")) || 1,
        sort: searchParams.get("sort") || "",
    });
    const [data, setData] = useState({
        page: paramsRef.current.page,
        size: 10,
        total: 0,
        offset: 0,
        totalElements: 0,
        prev: false,
        next: false,
        items: []
    });
    const cancelRequest = useMemo(() => props.onHandleCancelRequest, [props.onHandleCancelRequest]);
    const isEmptyState = useMemo(() => data.status === true, [data.status]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [anchorEl, setAnchorEl] = useState(false);

    const onHandleGetList = useCallback(async () => {
        let response = null;

        setData({
            page: paramsRef.current.page,
            size: 10,
            total: 0,
            offset: 0,
            totalElements: 0,
            prev: false,
            next: false,
            items: []
        });

        try {
            const payload = { ...paramsRef.current, size: 10 };

            memoSetSearchParams.current(sanitizeObject(payload), { replace: true });

            payload.page = paramsRef.current.page - 1;

            response = await api.get.reminderTemplate.list(sanitizeObject(payload));
        } catch (error) {
            serveRequestErrors(error);
        }

        if (response) {
            setData((prev) => ({
                ...prev,
                page: paramsRef.current.page,
                prev: !response.first,
                next: !response.last,
                items: response.content,
                total: response.totalPages,
                totalElements: response.totalElements,
                offset: response?.pageable?.offset || 0,
                status: response?.empty,
            }));
        }
    }, []);

    const onHandlePagination = useCallback((event) => {
        const control = event.currentTarget?.getAttribute("data-ctrl");

        if (control) {
            if (control === "prev") {
                if (paramsRef.current.page <= 1) return;
                paramsRef.current.page -= 1;
            } else {
                if (paramsRef.current.page >= data.total) return;
                paramsRef.current.page += 1;
            }
        } else {
            paramsRef.current.page = event.target.value;
        }

        onHandleGetList();
    }, [onHandleGetList, data.total]);

    const onHandleSort = useCallback((id, order) => {
        paramsRef.current.sort = order ? id + "," + order : "";
        paramsRef.current.page = 1;
        onHandleGetList();
    }, [onHandleGetList]);

    const onHandleCreate = useCallback(() => {
        modalRef.current?.onHandleShow();
    }, []);

    const onHandleEdit = useCallback(() => {
        setAnchorEl(null);
        modalRef.current?.onHandleShow(selectedTemplate);
    }, [selectedTemplate]);

    const onHandleDelete = useCallback(() => {
        setAnchorEl(null);
        confirmDeleteRef.current.onHandleShow(selectedTemplate.id);
    }, [selectedTemplate]);

    // Table columns
    const NameCell = useCallback(({ row }) => {
        return (
            <div className="name-column">
                <AppTableCell left value={row.original.templateName} />
            </div>
        );
    }, []);

    const DaysCell = useCallback(({ row }) => {
        const daysText = row.original.reminderDays
            ?.map(d => d.reminderDays)
            .sort((a, b) => b - a)
            .join(", ");

        return (
            <div className="days-column">
                <AppTableCell left value={daysText ? `${daysText} days` : "-"} />
            </div>
        );
    }, []);

    //prettier-ignore
    const StatusCell = useCallback(({ row }) => {
        const isActive = row.original.isActive;

        return <AppStatus status={isActive === true ? "ACTIVE" : "INACTIVE"} />;
    }, []);

    //prettier-ignore
    const MenuCell = useCallback((obj) => {
        return <AppTableMoreIcon onClick={(event) => onHandleTableMenu(event, obj)} />;
    }, []);

    const onHandleTableMenu = (event, { row }) => {
        setSelectedTemplate(row.original);
        setAnchorEl(event.currentTarget);
    };

    const onHandleCloseActionMenu = () => {
        setAnchorEl(null);
    };

    const columns = useMemo(
        () => [
            {
                Header: "Template Name",
                accessor: "templateName",
                disableSortBy: false,
                Cell: NameCell,
            },
            {
                Header: "Reminder Days",
                accessor: "reminderDays",
                disableSortBy: true,
                Cell: DaysCell,
            },
            {
                Header: "Status",
                accessor: "isActive",
                disableSortBy: false,
                Cell: StatusCell,
            },
            {
                Header: "",
                accessor: "*",
                disableSortBy: true,
                Cell: MenuCell,
            },
        ],
        [NameCell, DaysCell, StatusCell, MenuCell]
    );

    //prettier-ignore
    const headerButtons = useMemo(() => {
        const buttons = [
            { label: "Create Template", icon: addIcon, onClick: onHandleCreate },
        ];
        return buttons;
    }, [onHandleCreate]);

    useEffect(() => {
        onHandleGetList();
    }, [onHandleGetList]);

    useEffect(() => {
        return () => {
            cancelRequest(COMMON.ENDPOINT_PATH.REMINDER_TEMPLATE.LIST);
        };
    }, [cancelRequest]);

    return (
        <div className="page-reminder-template">
            <div className="reminder-template">
                <AppSearchInput buttons={headerButtons} />

                <div className="reminder-template__info">
                    <p className="reminder-template__info-title">How Reminder Templates Work</p>

                    <p className="reminder-template__info-text">Templates define when reminders are sent before an assignment's end date. For example, a template with "90, 60, 30, 14 days" will send notifications 3 months, 2 months, 1 month, and 2 weeks before the deadline. Users select a template when creating assignments.</p>
                </div>

                {isEmptyState && <AppEmptyState title="No records found" description="You don’t have any reminder templates yet" />}

                {!isEmptyState && (
                    <Fragment>
                        <Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseActionMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
                            <MenuItem onClick={onHandleEdit}>
                                <img className="app-table-menu__icon" src={editIcon} alt="Edit" />
                                Edit
                            </MenuItem>
                            <MenuItem onClick={() => onHandleDelete()}>
                                <img className="app-table-menu__icon" src={removeIcon} alt="delete" />
                                Delete
                            </MenuItem>
                        </Menu>

                        <AppTable columns={columns} pages={data} onHandlePagination={onHandlePagination} onHandleSort={onHandleSort} />
                    </Fragment>
                )}
            </div>

            <AppReminderTemplateModal ref={modalRef} onHandleGetList={onHandleGetList} />

            <AppConfirmDeleteTemplateModal ref={confirmDeleteRef} onHandleGetList={onHandleGetList} />
        </div>
    );
};

export default PageReminderTemplate;